import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/admin.service';
import Badge from '../../components/common/Badge';

const formatBroadcastDate = (dateStr: string | null) => {
  if (!dateStr) return 'Pending...';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminWithdrawals: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('ALL');

  // Frontend Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchWithdrawals = async () => {
    try {
      const res = await adminService.getWithdrawals();
      setItems(Array.isArray(res) ? res : (res?.data || []));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 0: return 'PENDING';
      case 1: return 'APPROVED';
      case 2: return 'REJECTED';
      default: return 'UNKNOWN';
    }
  };

  const handleApprove = async (id: number) => {
    setProcessing(id);
    try {
      await adminService.approveWithdraw(id);
      await fetchWithdrawals();
    } catch (err: any) {
      alert(`Approval Failed: ${err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id: number) => {
    const reason = window.prompt("REJECT PROTOCOL: Specify breach detail");
    if (reason === null) return;
    setProcessing(id);
    try {
      await adminService.rejectWithdraw(id, reason || "Protocol Violation");
      await fetchWithdrawals();
    } catch (err: any) {
      alert(`Rejection Failed: ${err.message}`);
    } finally {
      setProcessing(null);
    }
  };

  const filteredItems = items.filter(w => {
    const statusLabel = getStatusLabel(w.status);
    const userString = `${w.fname} ${w.lname} ${w.email} ${w.userId}`.toLowerCase();
    const matchesSearch = userString.includes(searchQuery.toLowerCase()) || w.btcAddress?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'ALL' || statusLabel === filter;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) return <div className="h-96 flex items-center justify-center"><div className="w-12 h-12 border-4 border-purple-600 border-t-transparent animate-spin rounded-full"></div></div>;

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 px-2">
        <div>
          <h2 className="text-4xl font-black text-white uppercase tracking-tighter">Asset Outflow</h2>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em] mt-2">Broadcast Distribution Hub</p>
        </div>
        <div className="flex flex-col md:flex-row gap-4 w-full lg:max-w-2xl">
          <input 
            type="text" 
            placeholder="Search Recipient, UID or Hash..." 
            value={searchQuery} 
            onChange={(e) => {setSearchQuery(e.target.value); setCurrentPage(1);}} 
            className="flex-1 bg-slate-900/50 border border-white/5 rounded-2xl px-6 py-4 text-white text-xs font-bold outline-none focus:ring-2 focus:ring-purple-600/40" 
          />
          <select 
            value={filter} 
            onChange={e => {setFilter(e.target.value as any); setCurrentPage(1);}} 
            className="bg-slate-900/80 border border-white/5 rounded-2xl px-6 py-4 text-white text-[10px] font-black uppercase tracking-widest outline-none"
          >
            <option value="ALL">All States</option>
            <option value="PENDING">Pending Only</option>
            <option value="APPROVED">Broadcasted</option>
            <option value="REJECTED">Blocked</option>
          </select>
        </div>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-3xl rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-600 uppercase text-[9px] tracking-[0.2em] font-black border-b border-white/5">
              <tr>
                <th className="px-10 py-8">Recipient Node</th>
                <th className="px-10 py-8">Value (USD)</th>
                <th className="px-10 py-8">Network Hash</th>
                <th className="px-10 py-8">Status</th>
                <th className="px-10 py-8">Broadcast Date</th>
                <th className="px-10 py-8">Control</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {currentItems.length > 0 ? currentItems.map((w) => {
                const statusLabel = getStatusLabel(w.status);
                const isActive = w.activeStatus === true || w.activeStatus === 1;

                return (
                  <tr key={w.wdrId} className="hover:bg-purple-600/5 transition-colors group">
                    <td className="px-10 py-6">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-white font-bold text-sm tracking-tight">
                            {w.fname} {w.lname}
                          </p>
                          {w.aga === 1 && (
                            <svg className="w-3.5 h-3.5 text-amber-500 fill-current" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          )}
                          <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-rose-500 shadow-[0_0_8px_#f43f5e]'}`}></span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-mono tracking-tight">{w.email}</p>
                        <p className="text-[9px] text-slate-600 font-black uppercase tracking-widest">UID: {w.userId}</p>
                      </div>
                    </td>
                    <td className="px-10 py-6">
                      <p className="text-emerald-500 font-black text-lg">${Number(w.amount || 0).toFixed(2)}</p>
                    </td>
                    <td className="px-10 py-6 font-mono text-xs text-slate-500 max-w-[200px] truncate group-hover:text-slate-300">{w.btcAddress}</td>
                    <td className="px-10 py-6">
                      <Badge 
                        label={statusLabel} 
                        variant={statusLabel === 'APPROVED' ? 'success' : statusLabel === 'PENDING' ? 'warning' : 'error'} 
                      />
                    </td>
                    <td className="px-10 py-6 text-[10px] text-slate-500 font-bold uppercase">{formatBroadcastDate(w.createdAt)}</td>
                    <td className="px-10 py-6">
                      {w.status === 0 ? (
                        <div className="flex items-center space-x-2">
                          <button 
                            disabled={processing === w.wdrId} 
                            onClick={() => handleApprove(w.wdrId)} 
                            className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all hover:shadow-[0_0_15px_rgba(16,185,129,0.3)] active:scale-95 disabled:opacity-30"
                          >
                            Release
                          </button>
                          <button 
                            disabled={processing === w.wdrId} 
                            onClick={() => handleReject(w.wdrId)} 
                            className="px-5 py-2.5 bg-rose-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all hover:shadow-[0_0_15px_rgba(225,29,72,0.3)] active:scale-95 disabled:opacity-30"
                          >
                            Block
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-700 font-black uppercase tracking-widest italic">
                          {w.status === 1 ? 'Complete' : 'Revoked'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={6} className="px-10 py-32 text-center text-slate-500 uppercase text-[10px] font-black tracking-widest">
                    <div className="flex flex-col items-center opacity-30">
                       <svg className="w-16 h-16 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                       No matching assets in this filter
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-10 py-8 bg-slate-950/40 border-t border-white/5 flex items-center justify-between">
            <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Archive Page {currentPage} of {totalPages}</span>
            <div className="flex space-x-2">
               <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-5 py-2.5 bg-slate-900 border border-white/5 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white disabled:opacity-30" disabled={currentPage === 1}>Prev</button>
               <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-5 py-2.5 bg-slate-900 border border-white/5 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white disabled:opacity-30" disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWithdrawals;